import { ArchiveIcon, LinkBreakIcon, PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { AddToProjectSubmenu } from '@/components/plans/AddToProjectSubmenu';
import { Button } from '@/components/ui/button';
import { Confirm } from '@/components/ui/confirm';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/use-auth';
import { clearDiscoverSessionId, peekDiscoverSessionId } from '@/lib/chat/discover-session-id';
import { DIALOG_PINNED_FOOTER, MENU_ITEM_MOBILE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { aiSessionService } from '@/services/data/AiSessionService';
import { logger } from '@/utils/logger';

interface ChatSessionMenuProps {
  /** The `ai_sessions` row this menu acts on. Null = no active conversation. */
  sessionId: string | null;
  /** Current title — prefills Rename and names the delete confirm. */
  title?: string;
  /** Project the conversation is linked to (shows "Remove from project"). */
  linkedPlanId?: string | null;
  /** Side the menu opens toward. Sidebar rows use `right` (ChatGPT pattern). */
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'end';
  sideOffset?: number;
  alignOffset?: number;
  contentClassName?: string;
  /** The trigger node — rendered via `asChild`. */
  children: React.ReactNode;
}

/**
 * Canonical conversation action menu for an `ai_sessions` row: Rename · Add /
 * Add to project · Archive · Delete. Reused by the sidebar recent-chats rows
 * and the create-page breadcrumb so the two surfaces never drift.
 *
 * Owns its own open state + delete Confirm dialog; the caller supplies only the
 * trigger appearance/placement. When the acted-on row is the conversation
 * currently open (`?resumeSession=` or the discover session), archive/delete
 * route back to a fresh chat.
 */
export function ChatSessionMenu({
  sessionId,
  title,
  linkedPlanId,
  side,
  align = 'end',
  sideOffset = 6,
  alignOffset,
  contentClassName,
  children,
}: ChatSessionMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const renameRequestedRef = useRef(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isActive =
    !!sessionId &&
    (searchParams.get('resumeSession') === sessionId || peekDiscoverSessionId() === sessionId);

  const refreshLists = (): void => {
    queryClient.invalidateQueries({ queryKey: ['recent-chat-sessions'] });
    queryClient.invalidateQueries({ queryKey: ['recent-chat-sessions-infinite'] });
    queryClient.invalidateQueries({ queryKey: ['project-conversations'] });
    queryClient.invalidateQueries({ queryKey: ['archive-items'] });
  };

  const evictRecentCaches = (): void => {
    // A newly mounted Home recent rail can otherwise render a dormant cached
    // row until its background refetch finishes. Never remove active sidebar
    // observers: TanStack cannot invalidate/refetch a query after removeQueries
    // destroys it, so the mounted rail would retain its stale result.
    queryClient.removeQueries({ queryKey: ['recent-chat-sessions'], type: 'inactive' });
    queryClient.removeQueries({
      queryKey: ['recent-chat-sessions-infinite'],
      type: 'inactive',
    });
  };

  const leaveActiveSession = (): void => {
    if (!isActive) return;
    if (sessionId && peekDiscoverSessionId() === sessionId) clearDiscoverSessionId();
    navigate('/?new=true');
  };

  const handleRename = (): void => {
    setOpen(false);
    if (!sessionId) {
      toast.error('No active conversation to rename.');
      return;
    }
    setRenameValue(title ?? '');
    renameRequestedRef.current = true;
    setRenameOpen(true);
  };

  const handleSubmitRename = async (): Promise<void> => {
    if (!sessionId) return;
    const trimmed = renameValue.trim();
    if (!trimmed || trimmed === title) {
      setRenameOpen(false);
      return;
    }
    setRenaming(true);
    const result = await aiSessionService.renameSession(sessionId, trimmed);
    setRenaming(false);
    if (result.error) {
      logger.error('[ChatSessionMenu] rename failed', result.error);
      toast.error('Could not rename conversation.');
      return;
    }
    setRenameOpen(false);
    queryClient.invalidateQueries({ queryKey: ['ai-session-title', sessionId] });
    refreshLists();
    toast.success('Renamed conversation.');
  };

  const handleAttach = async (planId: string | null): Promise<void> => {
    setOpen(false);
    if (!sessionId) {
      toast.error('No active conversation to move.');
      return;
    }
    const result = await aiSessionService.attachToPlan(sessionId, planId);
    if (result.error) {
      logger.error('[ChatSessionMenu] attach to plan failed', result.error);
      toast.error(planId ? 'Could not add to campaign.' : 'Could not remove from campaign.');
      return;
    }
    refreshLists();
    queryClient.invalidateQueries({ queryKey: ['campaigns', user?.id] });
    queryClient.invalidateQueries({ queryKey: ['chat-session-by-plan'] });
    toast.success(planId ? 'Added to campaign' : 'Removed from campaign');
  };

  const handleUndoArchive = async (id: string): Promise<void> => {
    const undo = await aiSessionService.restoreSession(id);
    if (undo.error) {
      toast.error('Could not restore conversation.');
      return;
    }
    refreshLists();
  };

  const handleArchive = async (): Promise<void> => {
    setOpen(false);
    if (!sessionId) return;
    const result = await aiSessionService.archiveSession(sessionId);
    if (result.error) {
      logger.error('[ChatSessionMenu] archive failed', result.error);
      toast.error('Could not archive conversation.');
      return;
    }
    evictRecentCaches();
    refreshLists();
    leaveActiveSession();
    toast.success('Conversation archived.', {
      action: { label: 'Undo', onClick: () => void handleUndoArchive(sessionId) },
    });
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!sessionId) throw new Error('No active conversation to delete.');
    const result = await aiSessionService.deleteSession(sessionId);
    if (result.error) {
      logger.error('[ChatSessionMenu] delete failed', result.error);
      throw new Error('Could not delete conversation.');
    }
    evictRecentCaches();
    refreshLists();
    leaveActiveSession();
    toast.success('Conversation deleted.');
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        className={cn(
          'w-[210px] max-md:w-[min(280px,var(--radix-dropdown-menu-content-available-width))]',
          contentClassName,
        )}
        onCloseAutoFocus={(event) => {
          if (renameRequestedRef.current) event.preventDefault();
        }}
      >
        <DropdownMenuItem onSelect={handleRename} className={MENU_ITEM_MOBILE}>
          <PencilSimpleIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
          <span>Rename</span>
        </DropdownMenuItem>
        {linkedPlanId && (
          <DropdownMenuItem onSelect={() => void handleAttach(null)} className={MENU_ITEM_MOBILE}>
            <LinkBreakIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
            <span>Remove from campaign</span>
          </DropdownMenuItem>
        )}
        <AddToProjectSubmenu
          currentProjectId={linkedPlanId}
          onSelectProject={(planId) => void handleAttach(planId)}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void handleArchive()} className={MENU_ITEM_MOBILE}>
          <ArchiveIcon className="size-3.5 text-muted-foreground/70" weight="regular" />
          <span>Archive</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            setOpen(false);
            setConfirmDeleteOpen(true);
          }}
          className={cn(MENU_ITEM_MOBILE, 'text-destructive focus:text-destructive')}
        >
          <TrashIcon className="size-3.5" weight="regular" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
      <Dialog open={renameOpen} onOpenChange={(next) => !renaming && setRenameOpen(next)}>
        <DialogContent
          size="sm"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            renameRequestedRef.current = false;
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmitRename();
            }}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>Rename conversation</DialogTitle>
            </DialogHeader>
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              aria-label="Conversation name"
              maxLength={120}
              autoComplete="off"
              disabled={renaming}
            />
            <DialogFooter className={DIALOG_PINNED_FOOTER}>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameOpen(false)}
                disabled={renaming}
              >
                Cancel
              </Button>
              <Button type="submit" variant="neutral" disabled={renaming || !renameValue.trim()}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Confirm
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        variant="destructive"
        title="Delete conversation?"
        description={`This deletes "${title?.trim() || 'this conversation'}" and its messages, including any documents written in the chat. Posts and images it made are kept. This can't be undone.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
      />
    </DropdownMenu>
  );
}

export default ChatSessionMenu;
